import { getForeignKeys, getIdColumn } from "@cervval.js/common-sql-core";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { addToSetMap } from "@cervval.js/common/src/tools/collection.map.tools";
import { TimestampTools, anyReallyAny } from "@cervval.js/common/tools";
import { ActionPersistData, ActionPersistResult } from "../../action.model";
import { updateForeignIds } from "../../action.tools";
import { ACTION_PERSISTENCE_SERVICE, ActionPersistenceService } from "../actionPersistence.service";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "../cache.service";
import { TestElementTypes } from "../cache.tools.spec";

/**
 * ActionPersistenceService that does nothing
 */
@Injectable(ACTION_PERSISTENCE_SERVICE)
export class DummyActionPersistenceService<Context = void> implements ActionPersistenceService<Context> {

    public nextPositiveId: number = 1000;

    /**
     * Action Cache service
     */
    @Injected(ACTION_CACHE_SERVICE)
    public readonly actionCacheService!: ActionCacheService<TestElementTypes>;

    /**
     * next id
     */
    protected _nextId(elementTypeName: string, currentId: number): number {
        return this.nextPositiveId++;
    }

    /**
     * process create operations
     */
    protected _createElements(action: ActionPersistData<Context>, result: ActionPersistResult, elementsByTypeName: Map<string, Set<unknown>>): void {
        if (action.elementsToCreate) {
            // generate a positive id for each created element
            for (const e of action.elementsToCreate) {
                const id = e.element ? getProp(e.element, "id") : undefined;
                let newId: number | undefined;
                if (typeof id === "number") {
                    if (e.isRecreate && id < 0) {
                        getLogConsole().error("The element to be recreated has a negative identifier");
                    }
                    newId = this._nextId(e.elementTypeName, id);
                    result.updatedIds[e.elementTypeName] ??= {};
                    result.updatedIds[e.elementTypeName][id] = newId;
                }
                // add elements to map to update their foreign keys
                addToSetMap(elementsByTypeName, e.elementTypeName, e.element);

                // add to summary
                const eltSummary = result.updateSummary[e.elementTypeName] ??= {};
                if (newId != null) {
                    const createdIds = eltSummary.createdIds ??= [];
                    createdIds.push(newId);
                } else {
                    // creation did not lead to a new id => mark all dirty
                    eltSummary.global = true;
                }
            }
        }
    }

    /**
     * process update operations
     */
    protected _updateElements(action: ActionPersistData<Context>, result: ActionPersistResult, elementsByTypeName: Map<string, Set<unknown>>): void {
        if (action.elementsToUpdate) {
            // add elements to map to update their foreign keys and fill summary
            for (const e of action.elementsToUpdate) {
                const element = this.actionCacheService.getDTOCacheRepository(e.elementTypeName as anyReallyAny).findDTOById(e.elementId);
                if (element) {
                    addToSetMap(elementsByTypeName, e.elementTypeName, element);

                    // add to summary
                    const eltSummary = result.updateSummary[e.elementTypeName] ??= {};
                    const updatedIds = eltSummary.updatedIds ??= [];
                    updatedIds.push(e.elementId);
                }
            }
        }
    }

    /**
     * process delete operations
     */
    protected _deleteElements(action: ActionPersistData<Context>, result: ActionPersistResult, elementsByTypeName: Map<string, Set<unknown>>): void {
        if (action.elementsToDelete) {
            // simply fill summary
            for (const e of action.elementsToDelete) {
                const eltSummary = result.updateSummary[e.elementTypeName] ??= {};
                const deletedIds = eltSummary.deletedIds ??= [];
                deletedIds.push(e.elementId);
            }
        }
    }

    /**
     * Persist actions (generate ids for created objects and update foreign keys)
     */
    public persistActionData(actionPersistData: ActionPersistData<Context>[]): Promise<ActionPersistResult[]> {
        return new Promise<void>((resolve) => {
            // introduce artificial delay to simulation persistence
            setTimeout(resolve, 20);
        }).then(() => {
            const results: ActionPersistResult[] = [];
            for (const a of actionPersistData) {
                const result: ActionPersistResult = {
                    time: TimestampTools.NOW(),
                    updateSummary: {},
                    updatedIds: {}
                };
                const elementsByTypeName: Map<string, Set<unknown>> = new Map();
                this._createElements(a, result, elementsByTypeName);
                this._updateElements(a, result, elementsByTypeName);
                this._deleteElements(a, result, elementsByTypeName);

                // update Foreign Ids
                for (const elementTypeName of elementsByTypeName.keys()) {
                    const td = this.actionCacheService.getTableDefinition(elementTypeName as anyReallyAny);
                    if (td) {
                        // gather foreignKeys
                        const foreignKeys = getForeignKeys(td);

                        // register updated ids in result (does not touch elements)
                        updateForeignIds({
                            elements: Array.from(elementsByTypeName.get(elementTypeName) ?? []),
                            elementTypeName,
                            foreignKeys,
                            idColumnName: getIdColumn(td),
                            result,
                            updateElements: false// leave elements unmodified (they will be updated by encapsulateWithAction if needed)
                        });
                    }
                }

                results.push(result);
            }
            return Promise.resolve(results);
        });
    }

}
